import { useEffect, useRef, useState } from "react";
import useAuth from "../../../hook/useAuth";
import axios from "../../../config/axios";
import baseURL from "../../../config/api";
import { useLocation, useNavigate } from "react-router-dom";
import EmailValidatedToCheckExistComponent from "../../../components/EmailValidatedToCheckExistComponent";
import { DoesEmailExist, DoesUserNameExist } from "../../../services/Users";
import UsernameValidatedComponent from "../../../components/UsernameValidatedComponent";
import { PhoneNumberInput } from "../../../components/PhoneNumberInput";
import PasswordValidation from "../../../components/PasswordValidation";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";

export const EditUsers = () => {
    const navigate = useNavigate();
    const idNumber = useLocation();
    const { auth }: any = useAuth();
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [userName, setUserName] = useState<string>("");
    const [emailAddress, setEmailAddress] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [mobilPhoneNumber, setMobilePhoneNumber] = useState<string>("");
    const [usernameTouched, setUsernameTouched] = useState<boolean>(false);
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
    const [enabled, setEnabled] = useState<string>("Yes");
    const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy KK:mm a");
    const [roleId, setRoleId] = useState<string>("");
    const [roleOptions, setRoleOptions] = useState([]);
    const [bio, setBio] = useState<string>("");
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
    const [imagePath, setImagePath] = useState<string>("");
    const inputFileRef = useRef<HTMLInputElement>(null);
    const [uploadFileImg, setUploadFileImg] = useState<FileList | null>(null);
    const [userData, setUserData] = useState<any>(null);

    useEffect(() => {
        axios.get(`${baseURL}roles`)
            .then((response) => {
                setRoleOptions(
                    response?.data?.map((item: any) => ({
                        value: item.id,
                        label: item.roleName,
                    }))
                );
            })
            .catch(error => {
                console.error(error);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setPleaseWaitPopup(true);
        axios
            .get(`${baseURL}users/${idNumber?.state}`)
            .then((response) => {
                setUserData(response?.data);
                setFirstName(response?.data?.firstName);
                setLastName(response?.data?.lastName);
                setUserName(response?.data?.userName);
                setEmailAddress(response?.data?.emailAddress);
                setDateFormat(response?.data?.dateFormat);
                setPassword(response?.data?.password);
                setMobilePhoneNumber(response?.data?.mobilPhoneNumber);
                setEnabled(response?.data?.isActive ? "Yes" : "No");
                setRoleId(response?.data?.roleId);
                setImagePath(response?.data?.profilePic);
                setPleaseWaitPopup(false);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber]);

    const onFileChangeCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files as FileList;
        if (selectedFiles.length !== 0) {
            setUploadFileImg(selectedFiles);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWaitPopup(true);

        let uploadedImagePath = imagePath;

        if (uploadFileImg && uploadFileImg?.length > 0) {
            const formData = new FormData();
            formData.append("ImageFile", uploadFileImg[0]);

            try {
                const uploadRes = await axios.post(`${baseURL}commonFile/Upload`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: false,
                });

                uploadedImagePath = uploadRes?.data;
                setImagePath(uploadedImagePath);
            } catch (err) {
                console.error("Image upload failed:", err);
            }
        }

        await axios
            .put(
                `${baseURL}users?userId=${auth?.id}`,
                JSON.stringify({
                    ...userData,
                    firstName,
                    lastName,
                    userName,
                    password,
                    mobilPhoneNumber,
                    emailAddress,
                    profilePic: uploadedImagePath,
                    bio,
                    isActive: enabled === "Yes" ? true : false,
                    roleId,
                    dateFormat,
                    updatedAt: new Date().toLocaleString(),
                    updatedBy: auth?.givenname

                }),
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: false,
                }
            )
            .then((response) => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                console.error("Error:", error);
            });
    }

    return (
        <>
            <div className="w-full p-4 bg-white rounded-md">
                <h2 className={`text-lg font-semibold`}>Edit User</h2>

                <div className="mt-2 p-2">
                    <form onSubmit={(e) => handleSubmit(e)}>
                        <div className="w-full">
                            <div>
                                {uploadFileImg && uploadFileImg?.length > 0 && uploadFileImg?.item(0) ? (
                                    <img
                                        className="w-[100px] h-[100px] rounded-sm"
                                        alt="uploaded preview"
                                        src={URL.createObjectURL(uploadFileImg?.item(0)!)}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null;
                                            currentTarget.src = "./images/profile-placeholder.png";
                                        }}
                                    />
                                ) : (
                                    imagePath && (
                                        <img
                                            className="w-[100px] h-[100px] rounded-sm"
                                            alt="profileImg"
                                            src={`${baseURL}commonFile/Get?name=${imagePath}`}
                                            onError={({ currentTarget }) => {
                                                currentTarget.onerror = null;
                                                currentTarget.src = "./images/profile-placeholder.png";
                                            }}
                                        />
                                    )
                                )}

                                {/* Responsive Upload Section */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
                                    <div>
                                        <label className="text-xs font-medium text-gray-700">
                                            Upload Profile Picture
                                        </label>
                                        <input
                                            name="profilepic"
                                            type="file"
                                            accept="image/*"
                                            className="block w-full rounded border border-gray-300 px-3 py-2 shadow-sm outline-none text-sm placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:ring-offset-1 mt-1 bg-transparent"
                                            onChangeCapture={onFileChangeCapture}
                                            ref={inputFileRef}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Responsive Form Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-6">
                                {/* First Column */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-medium text-[#667085]">First Name</label>
                                        <input
                                            type="text"
                                            className="w-full px-[8px] py-[8px] border rounded-md border-[#D0D0D0] outline-none focus:border-[#167AC3] text-sm text-[#1D2939] bg-transparent"
                                            value={firstName}
                                            placeholder="First Name"
                                            onChange={(e) => setFirstName(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <EmailValidatedToCheckExistComponent
                                        email={emailAddress}
                                        setEmail={setEmailAddress}
                                        getEmail={DoesEmailExist}
                                        touched={emailTouched}
                                        setTouched={setEmailTouched}
                                        className={{
                                            parentDiv: "",
                                            label: "block text-xs font-medium text-[#667085]",
                                            input:
                                                "w-full px-[8px] py-[8px] border rounded-md border-[#D0D0D0] outline-none focus:border-[#167AC3] text-sm text-[#1D2939] bg-transparent",
                                        }}
                                    />

                                    <UsernameValidatedComponent
                                        userName={userName}
                                        setUserName={setUserName}
                                        getUsername={DoesUserNameExist}
                                        touched={usernameTouched}
                                        setTouched={setUsernameTouched}
                                        className={{
                                            parentDiv: "",
                                            label: "block text-xs font-medium text-[#667085]",
                                            input:
                                                "w-full px-[8px] py-[8px] border rounded-md border-[#D0D0D0] outline-none focus:border-[#167AC3] text-sm text-[#1D2939] bg-transparent",
                                        }}
                                    />

                                    <div>
                                        <label className="block text-xs font-medium text-[#667085]">Date Format</label>
                                        <select
                                            className={`w-full px-[8px] py-[8px] border rounded-md outline-none focus:border-[#167AC3] text-sm bg-transparent`}
                                            value={dateFormat}
                                            onChange={(e) => setDateFormat(e.target.value)}
                                        >
                                            <option value="MM/dd/yyyy KK:mm a">Use System Default</option>

                                        </select>
                                    </div>

                                    {/* Desktop View Field */}
                                    <div className="block max-sm:hidden">
                                        <label className="block text-xs font-medium text-[#667085]">Bio</label>
                                        <textarea
                                            rows={4}
                                            className="w-full px-[8px] py-[8px] border rounded-md border-[#D0D0D0] outline-none focus:border-[#167AC3] text-sm text-[#1D2939] bg-transparent"
                                            value={bio}
                                            placeholder="Bio"
                                            onChange={(e) => setBio(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Second Column */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-medium text-[#667085]">Last Name</label>
                                        <input
                                            type="text"
                                            className="w-full px-[8px] py-[8px] border rounded-md border-[#D0D0D0] outline-none focus:border-[#167AC3] text-sm text-[#1D2939] bg-transparent"
                                            value={lastName}
                                            placeholder="Last Name"
                                            onChange={(e) => setLastName(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-[#667085]">Mobile Number</label>
                                        <PhoneNumberInput
                                            name={"phoneNumber"}
                                            value={mobilPhoneNumber}
                                            setValue={setMobilePhoneNumber}
                                            fontSize={"14px"}
                                            required={true}
                                        />
                                    </div>


                                    <PasswordValidation
                                        password={password}
                                        setPassword={setPassword}
                                        isValid={isValidPassword}
                                        setIsValid={setIsValidPassword}
                                        touched={passwordTouched}
                                        setTouched={setPasswordTouched}
                                        className={{
                                            parentDiv: "relative",
                                            label: "block text-xs font-medium text-[#667085]",
                                            input:
                                                "w-full px-[8px] py-[8px] border rounded-md border-[#D0D0D0] outline-none focus:border-[#167AC3] text-sm text-[#1D2939] bg-transparent",
                                        }}
                                    />



                                    <div>
                                        <label className="block text-xs font-medium text-[#667085]">Role</label>
                                        <select
                                            className={`w-full px-[8px] py-[8px] border rounded-md outline-none focus:border-[#167AC3] text-sm bg-transparent`}
                                            value={roleId}
                                            onChange={(e) => setRoleId(e.target.value)}
                                        >
                                            {roleOptions?.map((role: any) => (
                                                <option key={role.value} value={role.value}>
                                                    {role.label}
                                                </option>
                                            ))}

                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-[#667085]">Active</label>
                                        <select
                                            className={`w-full px-[8px] py-[8px] border rounded-md outline-none focus:border-[#167AC3] text-sm bg-transparent`}
                                            value={enabled}
                                            onChange={(e) => setEnabled(e.target.value)}
                                        >
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>

                                        </select>
                                    </div>

                                </div>
                            </div>

                            {/* Mobile View Field */}
                            <div className="hidden max-sm:block mt-4">
                                <label className="block text-xs font-medium text-[#667085]">Bio</label>
                                <textarea
                                    rows={4}
                                    className="w-full px-[8px] py-[8px] border rounded-md border-[#D0D0D0] outline-none focus:border-[#167AC3] text-sm text-[#1D2939] bg-transparent"
                                    value={bio}
                                    placeholder="Bio"
                                    onChange={(e) => setBio(e.target.value)}
                                />
                            </div>

                            {/* Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 mt-6">
                                <button
                                    type="submit"
                                    className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate(-1)}
                                    className="w-full sm:w-auto px-4 py-2 rounded bg-[#FFFFFF] text-primary cursor-pointer border border-primary"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <PleasewaitPopup showPopup={pleaseWaitPopup} />
        </>
    )
}